import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Plus, Trash2, UserCheck, AlertCircle, User } from 'lucide-react';
import { FormButton } from '../common/Form';
import { useApp } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import { TripFormData } from './TripWizardModal';
import { Companion } from '../../types';
import { validateEmail } from '../../utils/emailValidation';
import { validateCPF, formatDocument } from '../../utils/helpers';
import { validateDocument, formatDocument as formatDocumentByType } from '../../utils/documentValidation';


interface Step2Props {
  data: TripFormData;
  onChange: (data: Partial<TripFormData>) => void;
  onNext: () => void;
  onPrev: () => void;
  isLoading: boolean;
}

interface TripParticipant {
  id?: number;
  name: string;
  email?: string;
  nationality?: string;
  document_type?: string;
  document_number: string;
  date_of_birth: string;
  phone?: string;
  country_code?: string;
  exemption_reason_id?: number;
  is_saved_companion: boolean;
}

export function Step2Participants({ data, onChange, onNext, onPrev, isLoading }: Step2Props) {
  const { companions } = useApp();
  const { user } = useAuth();
  const [selectedCompanions, setSelectedCompanions] = useState<number[]>([]);
  const [manualParticipants, setManualParticipants] = useState<TripParticipant[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [userParticipates, setUserParticipates] = useState(true);
  const [emailErrors, setEmailErrors] = useState<{[key: number]: string}>({});

  // Initialize from existing data
  useEffect(() => {
    if (data.participants.length > 0) {
      const savedIds: number[] = [];
      const manual: TripParticipant[] = [];
      let hasMainUser = false;

      data.participants.forEach(p => {
        if (p.is_saved_companion && p.id) {
          savedIds.push(p.id);
        } else {
          // Check if this is the main user
          if (user && p.name === user.name && p.email === user.email && p.document_number === user.document_number) {
            hasMainUser = true;
            // Don't add main user to manual participants - they should be handled separately
          } else {
            manual.push(p);
          }
        }
      });

      setSelectedCompanions(savedIds);
      setManualParticipants(manual);
      setUserParticipates(hasMainUser);
    }
  }, [data.participants, user]);

  const calculateAge = (birthDate: string): number => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  const isExemptByAge = (birthDate: string): boolean => {
    const age = calculateAge(birthDate);
    return age < 12 || age >= 60;
  };

  const handleCompanionToggle = (companionId: number) => {
    setSelectedCompanions(prev => 
      prev.includes(companionId)
        ? prev.filter(id => id !== companionId)
        : [...prev, companionId]
    );
  };

  const handleMainUserToggle = () => {
    // Always remove the main user from manual participants to prevent editing
    setManualParticipants(prev => 
      prev.filter(p => !(user && p.name === user.name && p.email === user.email && p.document_number === user.document_number))
    );
    
    // Simply toggle the participation status - user data will be added directly in handleNext
    setUserParticipates(!userParticipates);
  };

  const addManualParticipant = () => {
    const newParticipant: TripParticipant = {
      name: '',
      email: '',
      nationality: 'BR',
      document_type: 'CPF',
      document_number: '',
      date_of_birth: '',
      phone: '',
      country_code: '+55',
      is_saved_companion: false,
    };
    setManualParticipants(prev => [...prev, newParticipant]);
    setShowAddForm(true);
  };

  const updateManualParticipant = (index: number, field: keyof TripParticipant, value: string | number) => {
    setManualParticipants(prev =>
      prev.map((p, i) => {
        if (i === index) {
          const updatedParticipant = { ...p, [field]: value };
          
          // Auto-format document based on type
          if (field === 'document_number' || field === 'document_type') {
            const docType = field === 'document_type' ? value as string : p.document_type || 'CPF';
            const docNumber = field === 'document_number' ? value as string : p.document_number;
            
            if (docType === 'CPF' && docNumber) {
              updatedParticipant.document_number = formatDocument(docNumber);
            }
          }
          
          // Email validation
          if (field === 'email' && value) {
            const emailValue = value as string;
            if (emailValue.trim() && !validateEmail(emailValue)) {
              setEmailErrors(prev => ({ ...prev, [index]: 'Email inválido' }));
            } else {
              setEmailErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[index];
                return newErrors;
              });
            }
          }
          
          // Clear email validation error when email is cleared
          if (field === 'email' && !value) {
            setEmailErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors[index];
              return newErrors;
            });
          }
          
          return updatedParticipant;
        }
        return p;
      })
    );
  };

  const removeManualParticipant = (index: number) => {
    setManualParticipants(prev => prev.filter((_, i) => i !== index));
    
    // Clear email error for the removed participant and adjust indices
    setEmailErrors(prev => {
      const newErrors: {[key: number]: string} = {};
      Object.keys(prev).forEach(key => {
        const keyIndex = parseInt(key);
        if (keyIndex < index) {
          // Keep errors for participants before the removed one
          newErrors[keyIndex] = prev[keyIndex];
        } else if (keyIndex > index) {
          // Shift down errors for participants after the removed one
          newErrors[keyIndex - 1] = prev[keyIndex];
        }
        // Skip the error for the removed participant (keyIndex === index)
      });
      return newErrors;
    });
  };

  const handleNext = () => {
    // Check for email validation errors
    const hasEmailErrors = Object.keys(emailErrors).length > 0;
    if (hasEmailErrors) {
      alert('Corrija os erros de email antes de continuar.');
      return; // Don't proceed if there are email errors
    }

    // Validate required fields and document numbers
    const manualErrors: string[] = [];
    
    manualParticipants.forEach((participant, index) => {
      if (participant.name && participant.document_number && participant.date_of_birth) {
        // Validate document based on type
        if (participant.document_type === 'CPF') {
          const cleanCPF = participant.document_number.replace(/\D/g, '');
          if (cleanCPF.length === 11 && !validateCPF(cleanCPF)) {
            manualErrors.push(`Acompanhante ${index + 1}: CPF inválido`);
          }
        }
        
        // Validate email if provided
        if (participant.email && participant.email.trim() && !validateEmail(participant.email)) {
          manualErrors.push(`Acompanhante ${index + 1}: Email inválido`);
        }
      }
    });

    if (manualErrors.length > 0) {
      alert('Corrija os seguintes erros:\n' + manualErrors.join('\n'));
      return;
    }

    // Combine selected companions and manual participants
    const allParticipants: TripParticipant[] = [];

    // Add main user if participating (always use data from auth context)
    if (userParticipates && user && user.document_number && user.date_of_birth) {
      allParticipants.push({
        name: user.name,
        email: user.email,
        nationality: (user as any).nationality || 'BR',
        document_type: (user as any).document_type || 'CPF',
        document_number: user.document_number,
        date_of_birth: user.date_of_birth,
        phone: user.phone || undefined,
        country_code: user.country_code || '+55',
        exemption_reason_id: isExemptByAge(user.date_of_birth) ? 1 : undefined,
        is_saved_companion: false,
      });
    }

    // Add selected saved companions
    selectedCompanions.forEach(companionId => {
      const companion = companions.find(c => c.id === companionId);
      if (companion) {
        allParticipants.push({
          id: companion.id,
          name: companion.name,
          email: companion.email || undefined,
          nationality: companion.nationality || 'BR',
          document_type: companion.document_type || 'CPF',
          document_number: companion.document_number,
          date_of_birth: companion.date_of_birth,
          phone: companion.phone || undefined,
          country_code: companion.country_code || '+55',
          exemption_reason_id: isExemptByAge(companion.date_of_birth) ? 1 : undefined, // Age exemption
          is_saved_companion: true,
        });
      }
    });

    // Add manual participants (excluding main user to prevent duplicates)
    manualParticipants.forEach(participant => {
      // Skip if this is the main user (shouldn't happen but safety check)
      const isMainUser = user && 
        participant.name === user.name && 
        participant.email === user.email && 
        participant.document_number === user.document_number;
      
      if (!isMainUser && participant.name && participant.document_number && participant.date_of_birth) {
        allParticipants.push({
          ...participant,
          exemption_reason_id: isExemptByAge(participant.date_of_birth) ? 1 : participant.exemption_reason_id,
        });
      }
    });

    onChange({ participants: allParticipants });
    onNext();
  };

  const getTotalParticipants = () => {
    const companionCount = selectedCompanions.length;
    const manualCount = manualParticipants.filter(p => 
      p.name && p.document_number && p.date_of_birth &&
      // Exclude main user from manual count to prevent double counting
      !(user && p.name === user.name && p.email === user.email && p.document_number === user.document_number)
    ).length;
    const userCount = userParticipates ? 1 : 0;
    return companionCount + manualCount + userCount;
  };

  const getExemptCount = () => {
    let count = 0;
    
    // Count main user if participating and exempt
    if (userParticipates && user && user.date_of_birth && isExemptByAge(user.date_of_birth)) {
      count++;
    }
    
    // Count exempt saved companions
    selectedCompanions.forEach(companionId => {
      const companion = companions.find(c => c.id === companionId);
      if (companion && isExemptByAge(companion.date_of_birth)) {
        count++;
      }
    });

    // Count exempt manual participants (excluding main user)
    manualParticipants.forEach(participant => {
      const isMainUser = user && 
        participant.name === user.name && 
        participant.email === user.email && 
        participant.document_number === user.document_number;
      
      if (!isMainUser && participant.date_of_birth && isExemptByAge(participant.date_of_birth)) {
        count++;
      }
    });

    return count;
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <Users className="h-8 w-8 text-blue-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900">Participantes da Viagem</h3>
        <p className="text-gray-600 mt-2">Selecione quem irá participar da viagem</p>
      </div>

      {/* Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <UserCheck className="h-5 w-5 text-blue-600" />
            <span className="font-medium text-blue-900">
              {getTotalParticipants()} participante{getTotalParticipants() !== 1 ? 's' : ''} selecionado{getTotalParticipants() !== 1 ? 's' : ''}
            </span>
          </div>
          {getExemptCount() > 0 && (
            <div className="flex items-center space-x-1 text-green-700">
              <span className="text-sm">{getExemptCount()} isento{getExemptCount() !== 1 ? 's' : ''} por idade</span>
            </div>
          )}
        </div>
      </div>

      {/* Info sobre participação do usuário */}
      {user && user.document_number && user.date_of_birth ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <User className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h5 className="font-medium text-blue-900">Sua participação na viagem</h5>
              <p className="text-sm text-blue-700 mt-1">
                Você pode participar desta viagem. Use o botão abaixo para confirmar sua participação.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <h5 className="font-medium text-yellow-900">Complete seu perfil para participar</h5>
              <p className="text-sm text-yellow-700 mt-1">
                Para participar da viagem, você precisa ter CPF e data de nascimento cadastrados no seu perfil.
              </p>
              <div className="flex gap-2 mt-2">
                <Link 
                  to="/profile" 
                  className="text-xs bg-yellow-100 hover:bg-yellow-200 text-yellow-700 px-3 py-1 rounded-full transition-colors"
                >
                  Completar perfil
                </Link>
                <Link 
                  to="/companions" 
                  className="text-xs bg-yellow-100 hover:bg-yellow-200 text-yellow-700 px-3 py-1 rounded-full transition-colors"
                >
                  Cadastrar como acompanhante
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main User Participation */}
      {user && user.document_number && user.date_of_birth && (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Você</h4>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h5 className="font-medium text-gray-900">{user.name}</h5>
                  <p className="text-sm text-gray-600">{user.email}</p>
                  {user.date_of_birth && (
                    <p className="text-xs text-gray-500">
                      {calculateAge(user.date_of_birth)} anos
                      {isExemptByAge(user.date_of_birth) && (
                        <span className="ml-2 text-green-600 font-medium">• Isento</span>
                      )}
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={handleMainUserToggle}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  userParticipates
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {userParticipates ? 'Participando' : 'Não participa'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Saved Companions */}
      {companions.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Acompanhantes Salvos</h4>
          <div className="grid grid-cols-1 gap-4">
            {companions.map((companion) => {
              const isSelected = selectedCompanions.includes(companion.id);
              const age = calculateAge(companion.date_of_birth);
              const isExempt = isExemptByAge(companion.date_of_birth);

              return (
                <div
                  key={companion.id}
                  className={`
                    border rounded-lg p-4 cursor-pointer transition-all
                    ${isSelected 
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                  onClick={() => handleCompanionToggle(companion.id)}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-3 sm:space-y-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2">
                        <h5 className="font-medium text-gray-900 break-words">{companion.name}</h5>
                        {isExempt && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 self-start">
                            Isento
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1 break-words">
                        {age} anos • {companion.document_type || 'CPF'}: {companion.document_type === 'CPF' ? companion.document_number.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : companion.document_number}
                      </p>
                      {isExempt && (
                        <p className="text-xs text-green-600 mt-1">
                          {age < 12 ? 'Menor de 12 anos' : 'Maior de 60 anos'} - isento da taxa
                        </p>
                      )}
                    </div>
                    <div className={`
                      w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 self-start sm:self-center
                      ${isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}
                    `}>
                      {isSelected && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Manual Participants */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
          <h4 className="font-medium text-gray-900">Adicionar Acompanhante</h4>
          <FormButton
            type="button"
            variant="ghost"
            onClick={addManualParticipant}
            className="text-blue-600 hover:text-blue-700 self-start sm:self-auto"
          >
            <Plus className="h-4 w-4 mr-1" />
            Adicionar
          </FormButton>
        </div>

        {manualParticipants.map((participant, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-3 sm:p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h5 className="font-medium text-gray-900">Acompanhante {index + 1}</h5>
              <button
                onClick={() => removeManualParticipant(index)}
                className="text-red-500 hover:text-red-700 p-1"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  value={participant.name}
                  onChange={(e) => updateManualParticipant(index, 'name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                  placeholder="Nome do acompanhante"
                />
              </div>

              {/* Nationality */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nacionalidade *
                </label>
                <select
                  value={participant.nationality}
                  onChange={(e) => updateManualParticipant(index, 'nationality', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                >
                  <option value="BR">🇧🇷 Brasil</option>
                  <option value="US">🇺🇸 Estados Unidos</option>
                  <option value="GB">🇬🇧 Reino Unido</option>
                  <option value="FR">🇫🇷 França</option>
                  <option value="DE">🇩🇪 Alemanha</option>
                  <option value="ES">🇪🇸 Espanha</option>
                  <option value="IT">🇮🇹 Itália</option>
                  <option value="PT">🇵🇹 Portugal</option>
                  <option value="AR">🇦🇷 Argentina</option>
                  <option value="CL">🇨🇱 Chile</option>
                  <option value="CO">🇨🇴 Colômbia</option>
                  <option value="UY">🇺🇾 Uruguai</option>
                  <option value="PE">🇵🇪 Peru</option>
                  <option value="MX">🇲🇽 México</option>
                  <option value="CA">🇨🇦 Canadá</option>
                  <option value="JP">🇯🇵 Japão</option>
                  <option value="CN">🇨🇳 China</option>
                  <option value="IN">🇮🇳 Índia</option>
                  <option value="AU">🇦🇺 Austrália</option>
                  <option value="OTHER">🌐 Outro</option>
                </select>
              </div>

                {/* Document Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Documento *
                  </label>
                  <select
                    value={participant.document_type}
                    onChange={(e) => updateManualParticipant(index, 'document_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                  >
                    <option value="CPF">CPF</option>
                    <option value="Passaporte">Passaporte</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {participant.document_type === 'CPF' ? 'CPF' : 'Número do Documento'} *
                </label>
                <input
                  type="text"
                  value={participant.document_number}
                  onChange={(e) => updateManualParticipant(index, 'document_number', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                  placeholder={participant.document_type === 'CPF' ? '000.000.000-00' : 'Número do documento'}
                  maxLength={participant.document_type === 'CPF' ? 14 : 50}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Nascimento *
                </label>
                <input
                  type="date"
                  value={participant.date_of_birth}
                  onChange={(e) => updateManualParticipant(index, 'date_of_birth', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={participant.email}
                  onChange={(e) => updateManualParticipant(index, 'email', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base ${
                    emailErrors[index] ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="email@exemplo.com"
                />
                {emailErrors[index] && (
                  <p className="mt-1 text-sm text-red-600">{emailErrors[index]}</p>
                )}
              </div>

              <div className="sm:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone
                </label>
                <div className="flex space-x-2">
                  <select
                    value={participant.country_code}
                    onChange={(e) => updateManualParticipant(index, 'country_code', e.target.value)}
                    className="w-16 sm:w-20 px-1 sm:px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm"
                  >
                    <option value="+55">🇧🇷 +55</option>
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+33">🇫🇷 +33</option>
                    <option value="+49">🇩🇪 +49</option>
                    <option value="+34">🇪🇸 +34</option>
                    <option value="+39">🇮🇹 +39</option>
                    <option value="+351">🇵🇹 +351</option>
                    <option value="+54">🇦🇷 +54</option>
                  </select>
                  <input
                    type="tel"
                    value={participant.phone}
                    onChange={(e) => updateManualParticipant(index, 'phone', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>
            </div>

            {participant.date_of_birth && isExemptByAge(participant.date_of_birth) && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-800 font-medium">
                    Participante isento por idade ({calculateAge(participant.date_of_birth)} anos)
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Validation Message */}
      {getTotalParticipants() === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <p className="text-yellow-800">
              Selecione pelo menos um participante para continuar.
            </p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0 pt-6">
        <FormButton
          type="button"
          variant="ghost"
          onClick={onPrev}
          disabled={isLoading}
          className="order-2 sm:order-1"
        >
          Voltar
        </FormButton>
        
        <FormButton
          type="button"
          variant="primary"
          onClick={handleNext}
          disabled={isLoading || getTotalParticipants() === 0}
          className="min-w-[120px] order-1 sm:order-2"
        >
          Próximo
        </FormButton>
      </div>
    </div>
  );
}

export default Step2Participants;
